import { useEffect, useRef } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useFinalizeCheckoutDotComPaymentSession } from './useFinalizeCheckoutDotComPaymentSession';

/** Query param checkout.com appends to the success/failure URL when Flow redirects (e.g. after 3DS). */
const CKO_PAYMENT_ID_PARAM = 'cko-payment-id';

/** Hashes the Flow failure URLs return to — there's no card to store on a declined charge. */
const FAILURE_HASHES = ['#addCard_failure', '#topUp_failure', '#failure'];

/**
 * checkout.com FULL FLOW migration — REDIRECT half of finalize.
 *
 * Flow has two completion paths. When the payment session has `successUrl` / `failureUrl` set (ours
 * always does), checkout.com REDIRECTS the browser to that URL on completion — including the common
 * 3DS case — and the SDK's `onPaymentCompleted` callback NEVER fires. So the form's `onPaymentCompleted`
 * finalize only covers the rarer synchronous (no-redirect) path; the redirect path is finalized here.
 *
 * On redirect, checkout.com appends the `pay_...` id as `?cko-payment-id=` to the return URL. This hook
 * runs once at app root: if that param is present (and it's not a failure landing) it hands the id to
 * `finalizePaymentSession` so the backend stores the card / MIT consent immediately (instead of waiting
 * on the slow webhook, which also never reaches localhost), then strips the param so a refresh doesn't
 * re-finalize.
 *
 * Gated on the Full Flow path (the `flowPaymentsEnabled` LaunchDarkly flag being ON): finalize is
 * Full-Flow-specific — the Frames path processes the charge via `createCardPayment`, and a
 * `cko-payment-id` could appear on its 3DS return too, where finalizing would be wrong.
 */
export function useFinalizeOnPaymentRedirect(): void {
  const { flowPaymentsEnabled } = useFlags();
  const { finalizeSession } = useFinalizeCheckoutDotComPaymentSession();
  const ranRef = useRef(false);

  useEffect(() => {
    if (!flowPaymentsEnabled || ranRef.current) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const paymentId = params.get(CKO_PAYMENT_ID_PARAM);
    // `cko-payment-id` is appended to BOTH the success and failure URLs — skip the failure landings.
    if (!paymentId || FAILURE_HASHES.includes(window.location.hash)) {
      return;
    }
    ranRef.current = true;

    (async () => {
      await finalizeSession(paymentId);
      // Remove only `cko-payment-id`, preserving the rest of the URL (incl. the #*_success hash that the
      // success modals key off) so a manual refresh doesn't fire finalize again.
      params.delete(CKO_PAYMENT_ID_PARAM);
      const search = params.toString();
      const newUrl = window.location.pathname + (search ? `?${search}` : '') + window.location.hash;
      window.history.replaceState(null, '', newUrl);
    })();
  }, [finalizeSession, flowPaymentsEnabled]);
}
